import accountListTemplate from './accountList.html';

/*******************************************************************************
 * @ngdoc component
 * @name  csm.books.InitialSetup.dom.component:accountList
 *
 * @param {ArrayCollection} acctList
 * List of `SetupAccount` objects.
 *
 * @param {string} accountLabel
 * Label for Account Name column. Default=SERVER.BooksWebConstants.accountLabel + ' Name'
 *
 * @param {function) addChangeHandler
 * Add/Change button handler takes the account info to be changed or `null` to
 * add a new account. Returns a promise resolved with the new account info.
 *
 * @param {Boolean} hideBalance
 * Hide account balance column and balances total flag.
 *
 * @description
 * Initial Setup component with a ui-grid of accounts to create for the new
 * organization. Includes buttons for adding, changing, and removing accounts
 * from the list.
 *******************************************************************************/
export const accountListComponent = {
  bindings: {
    acctList: '<',
    accountLabel: '@',
    hideBalance: '<',
    addChangeHandler: '<'
  },
  template: accountListTemplate,
  controller: controller
};

/*******************************************************************************
 * controller */
/**
 * Controller for the account list; sends its validator back to the main app.
 *******************************************************************************/
/* @ngInject */ function controller($injector, $scope) {
  var $ctrl = this;
  var $svcs = { $ctrl, $injector, $scope };
  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));

    /*----------------*/
    /* load datagrid. */
    /*----------------*/
    $ctrl.grid = $injector.invoke(buildGrid, null, $svcs);
    $ctrl.grid.data = $ctrl.acctList;
  };
}

/*******************************************************************************
 * initCtrl */
/**
 * $ctrl property initializer.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $svcs, $injector) {
  Object.assign($ctrl, {
    /*------------*/
    /* Properties */
    /*------------*/
    acctsTotal: 0, // Calculated total of account balances.

    /*----------*/
    /* Handlers */
    /*----------*/
    addChangeAcct: $injector.invoke(addChangeAcct, null, $svcs),
    calculateTotals: $injector.invoke(calculateTotals, null, $svcs),
    removeAcct: $injector.invoke(removeAcct, null, $svcs)
  });

  /*-----------*/
  /* Accessors */
  /*-----------*/
  Object.defineProperties($ctrl, {
    /*------------------------------------------*/
    /* selectedRow - Current row being edited   */
    /* in grid. `undefined` if grid not active. */
    /*------------------------------------------*/
    selectedRow: {
      get: () =>
        $ctrl.grid
          ? $ctrl.grid.api?.selection?.getSelectedGridRows()[0]
          : undefined
    }
  });
}

/*******************************************************************************
 * addChangeAcct */
/**
 * Calls `addChangeHandler` with the current row entity to change or `null` to
 * add a new account. `$ctrl.acctList` is updated with changes account info
 * `userAdded` flag is set for new accounts.
 *
 * @param  isNew  `true`=Add new item, `false`=Change selectedRow item.
 *******************************************************************************/
/* @ngInject */ function addChangeAcct($ctrl) {
  return function (isNew) {
    var acctInfo = isNew ? null : $ctrl.selectedRow.entity;

    $ctrl.addChangeHandler(acctInfo).then(function (newInfo) {
      if (isNew)
        $ctrl.acctList.addItem(Object.assign(newInfo, { userAdded: true }));
      else $ctrl.acctList.replaceItem(acctInfo, newInfo);
    });
  };
}

/*******************************************************************************
 * buildGrid */
/**
 * Returns grid definition. cells are not editable and row can be selected by
 * clicking. Double-click brings row up in account dialog.
 *
 * TODO: There is currently no way to navigate into the grid and select a row
 * TODO: with the keyboard. `cellNav` does it but requires navigating each cell.
 *******************************************************************************/
/* @ngInject */ function buildGrid(
  $ctrl,
  $scope,
  $templateCache,
  CommonUIGridTemplates,
  uiGridConstants,
  AppConfig,
  AppServices,
  SERVER
) {
  /*------------------------------*/
  /* Configure receipt item grid. */
  /*------------------------------*/
  var gridOptions = {
    columnDefs: [],
    enableColumnResizing: true,
    enableCellEdit: false,
    enableCellEditOnFocus: true,
    enableColumnMenus: false,
    enableSorting: false,
    enableFiltering: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableRowSelection: true,
    enableRowHeaderSelection: false,
    minRowsToShow: 12,
    multiSelect: false,
    rowTemplate: $templateCache
      .get('ui-grid/ui-grid-row')
      .replace(/^<div/, '<div ng-dblclick="grid.api.onDblClick(row)"'),
    onRegisterApi: function (api) {
      this.api = api;

      /*------------------------------------------*/
      /* Add double-click handler to row          */
      /* template for changing the selected item. */
      /*------------------------------------------*/
      api.onDblClick = function (row) {
        api.selection.selectRow(row.entity);
        $ctrl.addChangeAcct(false);
      };

      /*---------------------------------------------------*/
      /* Add listener to calculate totals when items are   */
      /* added or removed and when an item cell is edited. */
      /*---------------------------------------------------*/
      $scope.$on(
        'destroy',
        api.grid.registerDataChangeCallback($ctrl.calculateTotals, [
          uiGridConstants.dataChange.ROW,
          uiGridConstants.dataChange.EDIT
        ])
      );

      /*--------------------------------------------------------------------------*/
      /* Add listener to modify row selection on cell navigation.                 */
      /*                                                                          */
      /* https://github.com/angular-ui/ui-grid/issues/2788#issuecomment-175371524 */
      /*--------------------------------------------------------------------------*/
      $scope.$on(
        'destroy',
        api.cellNav.on.navigate(null, function (newRowCol, oldRowCol) {
          api.selection.selectRow(newRowCol.row.entity);
        })
      );
    }
  };

  /*----------------*/
  /* Account Number */
  /*----------------*/
  if (!AppConfig.hideAccountNumbers)
    gridOptions.columnDefs.push({
      displayName: 'Number',
      enableSorting: true,
      name: 'accountNumber',
      type: 'string',
      width: 100,
      allowCellFocus: false
    });

  /*--------------------------------------*/
  /* Account Name uses label provided or  */
  /* defaults to app configuration label. */
  /*--------------------------------------*/
  gridOptions.columnDefs.push({
    displayName:
      $ctrl.accountLabel || SERVER.BooksWebConstants.accountLabel + ' Name',
    name: 'accountName',
    enableSorting: true,
    type: 'string',
    minWidth: 300,
    allowCellFocus: false
  });

  /*--------------*/
  /* Account Type */
  /*--------------*/
  gridOptions.columnDefs.push({
    displayName: 'Type',
    name: 'type',
    enableSorting: true,
    type: 'string',
    width: 200,
    allowCellFocus: false
  });

  /*-----------------*/
  /* Balance column. */
  /*-----------------*/
  if (!$ctrl.hideBalance)
    gridOptions.columnDefs.push({
      displayName: 'Balance',
      name: 'currentBalance',
      type: 'number',
      cellClass: 'text-right',
      cellFilter: 'currency:"":2',
      width: 100,
      allowCellFocus: true,
      allowNegative: true,
      enableCellEdit: true,
      editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
      cellEditableCondition: function ($scope) {
        return AppServices.enableBalanceInput($scope.row.entity);
      }
    });

  return gridOptions;
}

/*******************************************************************************
 * $ctrl.calculateTotals */
/**
 * Calculates `acctsTotal`. Called when grid data is changed.
 *******************************************************************************/
/* @ngInject */ function calculateTotals($ctrl, Tools) {
  return function () {
    $ctrl.acctsTotal = Tools.roundMoney(
      $ctrl.acctList.reduce(function (total, acct) {
        return (total += acct.currentBalance);
      }, 0)
    );
  };
}

/*******************************************************************************
 * $ctrl.removeAcct */
/**
 * Remove button handler. Shows confirm dialog. Does not allow removal of default
 * accounts.
 *******************************************************************************/
/* @ngInject */ function removeAcct($ctrl, AppServices) {
  return function () {
    AppServices.removeAccountHandler($ctrl.selectedRow.entity).then(
      function (acct) {
        $ctrl.acctList.removeItem(acct);
      }
    );
  };
}
